import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "I'm the Dispatch Hunts AI assistant. Ask me about species, destinations, pricing, or how to get started." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/chat`, { message: text, session_id: sessionId });
      setMessages((prev) => [...prev, { role: "assistant", text: data.response }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", text: "Sorry, I'm having trouble connecting. Call 406-580-5366 for immediate help." }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* Toggle Button */}
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-brass text-carbon rounded-full shadow-xl flex items-center justify-center hover:bg-brass/90 transition-all"
          data-testid="chat-toggle-btn">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-chalk border border-carbon/20 shadow-2xl flex flex-col" style={{ height: "500px" }}
          data-testid="chat-window">
          {/* Header */}
          <div className="bg-carbon px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div>
              <p className="font-display text-sm font-bold text-bone">Dispatch Hunts AI</p>
              <p className="text-label text-[9px] tracking-wider uppercase text-bone/40">Ask about hunts, species, pricing</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-bone/60 hover:text-bone"><X className="w-5 h-5" /></button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] p-3 ${msg.role === "user" ? "bg-brass/10 text-carbon" : "bg-bone border border-carbon/10 text-carbon"}`}>
                  <p className="font-body text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-bone border border-carbon/10 p-3">
                  <Loader className="w-4 h-4 text-brass animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-carbon/10 p-3 flex gap-2 flex-shrink-0">
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Ask about a hunt..."
              className="flex-1 h-10 px-3 bg-bone border border-carbon/15 font-body text-sm text-carbon placeholder:text-slate/40 focus:outline-none focus:border-brass"
              data-testid="chat-input" />
            <button onClick={sendMessage} disabled={loading || !input.trim()}
              className="w-10 h-10 bg-brass text-carbon flex items-center justify-center hover:bg-brass/80 disabled:opacity-40"
              data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
